import {
  Box,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Slider,
  Typography
} from '@mui/material'
import { useId } from 'react'
import { OrganSegmentationType } from 'types/exam/types'
import AccordionBase from 'components/Accordion'
import { useIsMobile } from 'utils/responsive-design'
import {
  IKnownOrganSegmentation,
  ORGAN_OPACITY,
  ORGANS
} from '../constants/organs'

interface IOrganToggleList {
  segmentations: IKnownOrganSegmentation[]
  selected: OrganSegmentationType[]
  opacity: number
  onToggle: (type: OrganSegmentationType) => void
  onToggleAll: () => void
  onOpacityChange: (opacity: number) => void
  /**
   * `side`: painel lateral do modal, com título; `bottom`: abaixo da imagem, sem título e em mais
   * colunas. Abaixo de `md` os dois viram um accordion "Órgãos" fechado.
   */
  layout: 'side' | 'bottom'
}

const formatPercent = (value: number) => `${Math.round(value * 100)}%`

interface ISelectAll {
  selectedCount: number
  total: number
  onToggleAll: () => void
}

const SelectAll = ({ selectedCount, total, onToggleAll }: ISelectAll) => (
  <FormControlLabel
    control={
      <Checkbox
        size='small'
        checked={selectedCount === total}
        indeterminate={selectedCount > 0 && selectedCount < total}
        onChange={onToggleAll}
      />
    }
    label={<Typography variant='body2'>Selecionar todos</Typography>}
  />
)

interface IOpacityControl {
  opacity: number
  onOpacityChange: (opacity: number) => void
}

const OpacityControl = ({ opacity, onOpacityChange }: IOpacityControl) => {
  const labelId = useId()
  return (
    <Box display='flex' alignItems='center' gap={2} sx={{ minWidth: 240 }}>
      <Typography variant='body2' id={labelId}>
        Opacidade
      </Typography>
      <Slider
        size='small'
        aria-labelledby={labelId}
        value={opacity}
        min={ORGAN_OPACITY.min}
        max={ORGAN_OPACITY.max}
        step={ORGAN_OPACITY.step}
        marks
        valueLabelDisplay='auto'
        valueLabelFormat={formatPercent}
        getAriaValueText={formatPercent}
        onChange={(_, value) => {
          if (typeof value === 'number') onOpacityChange(value)
        }}
        sx={{ flex: 1 }}
      />
      <Typography variant='body2' sx={{ minWidth: 40, textAlign: 'right' }}>
        {formatPercent(opacity)}
      </Typography>
    </Box>
  )
}

const OrganToggleList: React.FC<IOrganToggleList> = ({
  segmentations,
  selected,
  opacity,
  onToggle,
  onToggleAll,
  onOpacityChange,
  layout
}) => {
  const isBottom = layout === 'bottom'
  // Em telas pequenas a seção começa fechada, para a imagem e o resultado virem primeiro.
  const isCollapsible = useIsMobile()
  const showLegend = !isBottom && !isCollapsible
  const selectedCount = segmentations.filter(({ segmentation_type }) =>
    selected.includes(segmentation_type)
  ).length

  const fieldset = (
    <FormControl
      component='fieldset'
      aria-label={showLegend ? undefined : 'Órgãos'}
      sx={{ mt: isCollapsible ? 0 : 2, width: '100%' }}
    >
      {showLegend && <FormLabel component='legend'>Órgãos</FormLabel>}
      {segmentations.length === 0 ? (
        <Typography variant='body2' color='textSecondary'>
          Órgãos não disponíveis para este exame
        </Typography>
      ) : (
        <>
          <Box
            display='flex'
            flexWrap='wrap'
            justifyContent='space-between'
            alignItems='center'
            columnGap={2}
          >
            <SelectAll
              selectedCount={selectedCount}
              total={segmentations.length}
              onToggleAll={onToggleAll}
            />
            <OpacityControl
              opacity={opacity}
              onOpacityChange={onOpacityChange}
            />
          </Box>
          <Divider sx={{ my: 1 }} />
          <FormGroup
            sx={{
              display: 'grid',
              // Colunas com a largura dos nomes, mas encolhíveis: `max-content` puro impede a
              // coluna da imagem de encolher e empurra o restante do modal para fora.
              gridTemplateColumns: isBottom
                ? 'repeat(auto-fill, minmax(170px, max-content))'
                : { xs: '1fr', sm: 'repeat(2, minmax(0, max-content))' },
              justifyContent: 'start',
              columnGap: 4
            }}
          >
            {segmentations.map(({ segmentation_type: type }) => {
              const { label, color } = ORGANS[type]
              return (
                <FormControlLabel
                  key={type}
                  control={
                    <Checkbox
                      size='small'
                      checked={selected.includes(type)}
                      onChange={() => onToggle(type)}
                    />
                  }
                  label={
                    <Box display='flex' alignItems='center' gap={1}>
                      <Box
                        aria-hidden
                        sx={{
                          width: 14,
                          height: 14,
                          flexShrink: 0,
                          borderRadius: 0.5,
                          bgcolor: color,
                          border: 1,
                          borderColor: 'divider'
                        }}
                      />
                      <Typography variant='body2'>{label}</Typography>
                    </Box>
                  }
                />
              )
            })}
          </FormGroup>
        </>
      )}
    </FormControl>
  )

  return isCollapsible ? (
    <AccordionBase title='Órgãos' defaultExpanded={false} sx={{ mt: 2 }}>
      {fieldset}
    </AccordionBase>
  ) : (
    fieldset
  )
}

export default OrganToggleList
